import React, { Suspense } from 'react';

import Message from 'components/base/Message/Message';
import Layout from 'components/styled/Layout/Layout';
import PositionsTableSkeleton from 'components/data-containers/PositionsTable/SkeletonLoader';
import { CompareModes } from 'context/entityContext';
import { ChartTypes } from 'types/shared';
import { TopicFilterNames } from 'components/TopicFilters/shared';
import useMedia from 'hooks/useMedia';

const CurrentPositionsTable = React.lazy(() =>
  import('components/data-containers/PositionsTable/CurrentPositionsTable')
);
const PeakPositionsTable = React.lazy(() =>
  import('components/data-containers/PositionsTable/PeakPositionsTable')
);

interface IDSPChartsProps {
  compareMode?: CompareModes | undefined;
}

const DSPCharts: React.FC<IDSPChartsProps> = (props) => {
  const { compareMode } = props;
  const isLargeScreen = useMedia('(min-width: 60em)');
  const isCompare = Boolean(compareMode);
  const allowedFilters: TopicFilterNames[] = ['country'];
  const chartType: ChartTypes = 'dsp';

  if (isCompare) {
    return (
      <Layout.MaxWidth>
        <Message>
          Unfortunately there are no other components available in compare mode
          on this topic for artists.
        </Message>
      </Layout.MaxWidth>
    );
  }

  return (
    // Note: Do not remove the next <div>. It avoids flexbox to double margins between generic components.
    <div>
      <Suspense
        fallback={
          <PositionsTableSkeleton
            title="Track Chart Positions"
            productType="track"
            variation="currentPosition"
            short
          />
        }
      >
        <CurrentPositionsTable
          title="Track Chart Positions"
          allowedFilters={allowedFilters}
          glossarySlug="charts-graphs-tables/position-table"
          productType="track"
          chartType={chartType}
          short
        />
      </Suspense>

      <Suspense
        fallback={
          <PositionsTableSkeleton
            title={`Track Peak Positions${isLargeScreen ? ' by Country' : ''}`}
            productType="track"
            variation="peakPosition"
          />
        }
      >
        <PeakPositionsTable
          title={`Track Peak Positions${isLargeScreen ? ' by Country' : ''}`}
          allowedFilters={allowedFilters}
          glossarySlug="charts-graphs-tables/position-table"
          productType="track"
          chartType={chartType}
        />
      </Suspense>

      <Suspense
        fallback={
          <PositionsTableSkeleton
            title="Release Chart Positions"
            productType="album"
            variation="currentPosition"
          />
        }
      >
        <CurrentPositionsTable
          title="Release Chart Positions"
          allowedFilters={allowedFilters}
          glossarySlug="charts-graphs-tables/position-table"
          productType="album"
          chartType={chartType}
        />
      </Suspense>

      <Suspense
        fallback={
          <PositionsTableSkeleton
            title={`Release Peak Positions${
              isLargeScreen ? ' by Country' : ''
            }`}
            productType="album"
            variation="currentPosition"
          />
        }
      >
        <PeakPositionsTable
          title={`Release Peak Positions${isLargeScreen ? ' by Country' : ''}`}
          allowedFilters={allowedFilters}
          glossarySlug="charts-graphs-tables/position-table"
          productType="album"
          chartType={chartType}
        />
      </Suspense>
    </div>
  );
};

export default DSPCharts;
